import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  FileText,
  BarChart3,
  User,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Users,
  CalendarDays,
  HelpCircle,
} from 'lucide-react';
import { useState } from 'react';
import { clsx } from 'clsx';
import { useAuth } from '../../contexts/AuthContext';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'My Leave', href: '/leave', icon: FileText },
  { name: 'Balances', href: '/balances', icon: BarChart3 },
  { name: 'Payslips', href: '/payslips', icon: CreditCard },
  { name: 'Calendar', href: '/calendar', icon: Calendar },
  { name: 'Profile', href: '/profile', icon: User },
  { name: 'Help', href: '/help', icon: HelpCircle },
];

const adminNavigation = [
  { name: 'Manage Users', href: '/admin/users', icon: Users },
  { name: 'Manage Payslips', href: '/admin/payslips', icon: CreditCard },
  { name: 'Manage Holidays', href: '/admin/holidays', icon: CalendarDays },
];

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Get display name and initials
  const displayName = user?.firstName || user?.email?.split('@')[0] || 'User';
  const initials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : displayName[0]?.toUpperCase() || 'U';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 transform bg-white shadow-lg transition-transform duration-200 ease-in-out lg:hidden',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <img src="/logo-hcfs.png" alt="HCFS Logo" className="h-10 w-auto" />
            <span className="text-lg font-bold text-primary-600">PeopleMX</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="mt-4 px-2">
          {navigation.map(item => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                )
              }
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </NavLink>
          ))}
          {/* Admin section */}
          {user?.role && ['ADMIN', 'DIRECTOR'].includes(user.role) && (
            <>
              <div className="mt-4 mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Administration
              </div>
              {adminNavigation.map(item => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    )
                  }
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        {/* Mobile sign out */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-4">
          <div className="mb-3 px-3 text-sm text-gray-500">
            {user?.email}
          </div>
          <button
            onClick={handleSignOut}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-5 w-5" />
            Sign out
          </button>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div
        className={clsx(
          'hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300',
          sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col border-r border-gray-200 bg-white">
          <div className={clsx(
            'flex h-16 items-center border-b border-gray-200',
            sidebarCollapsed ? 'justify-center px-2' : 'px-4'
          )}>
            <div className="flex items-center gap-2">
              <img src="/logo-hcfs.png" alt="HCFS Logo" className={clsx(
                'w-auto transition-all',
                sidebarCollapsed ? 'h-8' : 'h-10'
              )} />
              {!sidebarCollapsed && (
                <span className="text-lg font-bold text-primary-600">PeopleMX</span>
              )}
            </div>
          </div>
          <nav className={clsx('mt-4 flex-1', sidebarCollapsed ? 'px-1' : 'px-2')}>
            {navigation.map(item => (
              <NavLink
                key={item.name}
                to={item.href}
                title={sidebarCollapsed ? item.name : undefined}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center rounded-md py-2 text-sm font-medium transition-colors',
                    sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3',
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  )
                }
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && item.name}
              </NavLink>
            ))}
            {/* Admin section */}
            {user?.role && ['ADMIN', 'DIRECTOR'].includes(user.role) && (
              <>
                {!sidebarCollapsed && (
                  <div className="mt-4 mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Administration
                  </div>
                )}
                {sidebarCollapsed && <div className="my-2 border-t border-gray-200" />}
                {adminNavigation.map(item => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    title={sidebarCollapsed ? item.name : undefined}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center rounded-md py-2 text-sm font-medium transition-colors',
                        sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      )
                    }
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {!sidebarCollapsed && item.name}
                  </NavLink>
                ))}
              </>
            )}
          </nav>
          <div className={clsx('border-t border-gray-200', sidebarCollapsed ? 'p-2' : 'p-4')}>
            {!sidebarCollapsed && (
              <div className="mb-2 px-3 text-xs text-gray-500 truncate" title={user?.email}>
                {user?.email}
              </div>
            )}
            <button
              onClick={handleSignOut}
              title={sidebarCollapsed ? 'Sign out' : undefined}
              className={clsx(
                'flex w-full items-center rounded-md py-2 text-sm font-medium text-red-600 hover:bg-red-50',
                sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3'
              )}
            >
              <LogOut className="h-5 w-5 flex-shrink-0" />
              {!sidebarCollapsed && 'Sign out'}
            </button>
          </div>
          {/* Collapse toggle button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute -right-3 top-20 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm hover:bg-gray-50"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className={clsx(
        'transition-all duration-300',
        sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
      )}>
        {/* Top header */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-gray-200 bg-white px-4 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <div className="text-right">
              <span className="text-sm font-medium text-gray-900">Welcome, {displayName}</span>
              {user?.role && (
                <p className="text-xs text-gray-500">{user.role}</p>
              )}
            </div>
            <div className="h-10 w-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-medium">
              {initials}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
